import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import omnivore from '@mapbox/leaflet-omnivore';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

/**
 * Component to load and display KML data on the map
 */
const KMLLayer = ({ kmlUrl }) => {
  const map = useMap();
  const layerRef = useRef(null);
  const [isMapReady, setIsMapReady] = useState(false);

  useEffect(() => {
    // Check if map is fully initialized by verifying essential panes exist
    const checkMapReady = () => {
      try {
        const mapPane = map.getPane('mapPane');
        const markerPane = map.getPane('markerPane');
        if (mapPane && markerPane) {
          setIsMapReady(true);
          return true;
        }
        return false;
      } catch (e) {
        return false;
      }
    };

    // Try immediately
    if (!checkMapReady()) {
      // If not ready, wait and try again
      const timer = setTimeout(() => {
        checkMapReady();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [map]);

  useEffect(() => {
    if (!kmlUrl || !map || !isMapReady) return;

    // Remove existing layer if it exists
    if (layerRef.current) {
      try {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      } catch (error) {
        console.error('Error removing previous layer:', error);
      }
    }

    // Load KML file
    const kmlLayer = omnivore.kml(kmlUrl)
      .on('ready', function() {
        try {
          console.log('KML layer loaded successfully');
          // Fit map bounds to KML layer with padding
          const bounds = this.getBounds();
          if (bounds && bounds.isValid()) {
            setTimeout(() => {
              map.fitBounds(bounds, { padding: [50, 50] });
            }, 100);
          }
        } catch (error) {
          console.error('Error fitting bounds:', error);
        }
      })
      .on('error', function(err) {
        console.error('Error loading KML:', err);
      });

    // Add to map only when ready
    try {
      kmlLayer.addTo(map);
      layerRef.current = kmlLayer;
    } catch (error) {
      console.error('Error adding KML layer to map:', error);
    }

    // Cleanup function
    return () => {
      if (layerRef.current) {
        try {
          map.removeLayer(layerRef.current);
        } catch (error) {
          console.error('Error removing layer:', error);
        }
      }
    };
  }, [kmlUrl, map, isMapReady]);

  return null;
};

/**
 * KML Map Viewer Component
 *
 * Displays a KML file on an interactive Leaflet map
 *
 * @param {string} kmlPath - Path to the KML file (relative to public directory)
 * @param {number} height - Height of the map container in pixels (default: 500)
 * @param {array} center - Initial center coordinates [lat, lng] (default: [39.26, -76.79])
 * @param {number} zoom - Initial zoom level (default: 12)
 */
const KMLMapViewer = ({
  kmlPath = '/points.kml',
  height = 500,
  center = [39.26, -76.79], // Default to Ellicott City area
  zoom = 12
}) => {
  const containerRef = useRef(null);

  return (
    <div
      ref={containerRef}
      style={{
        height: `${height}px`,
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        zIndex: 0
      }}
    >
      <MapContainer
        center={center}
        zoom={zoom}
        style={{
          height: '100%',
          width: '100%',
          position: 'relative',
          zIndex: 0
        }}
        scrollWheelZoom={true}
        zoomControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={19}
        />
        <KMLLayer kmlUrl={kmlPath} />
      </MapContainer>
    </div>
  );
};

export default KMLMapViewer;
